import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceCalendar = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const upcomingDeadlines = [
    {
      id: 1,
      title: "PF Return Filing",
      date: "2025-01-25",
      type: "pf",
      priority: "high",
      description: "Monthly PF return submission deadline"
    },
    {
      id: 2,
      title: "ESI Contribution",
      date: "2025-01-21",
      type: "esi",
      priority: "high",
      description: "ESI monthly contribution payment"
    },
    {
      id: 3,
      title: "Professional Tax",
      date: "2025-01-30",
      type: "pt",
      priority: "medium",
      description: "State professional tax payment"
    },
    {
      id: 4,
      title: "TDS Return Q3",
      date: "2025-01-31",
      type: "tds",
      priority: "high",
      description: "Quarterly TDS return filing"
    },
    {
      id: 5,
      title: "Labour Welfare Fund",
      date: "2025-02-15",
      type: "lwf",
      priority: "medium",
      description: "Half-yearly LWF contribution"
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error bg-error/10 border-error/20';
      case 'medium':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'low':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'pf':
        return 'Banknote';
      case 'esi':
        return 'Heart';
      case 'pt':
        return 'FileText';
      case 'tds':
        return 'Calculator';
      case 'lwf':
        return 'Users';
      default:
        return 'Calendar';
    }
  };

  const getDaysUntilDeadline = (date) => {
    const today = new Date();
    const deadline = new Date(date);
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Calendar" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Compliance Calendar</h3>
            <p className="text-sm text-muted-foreground">Upcoming statutory deadlines</p>
          </div>
        </div>
        <Button variant="outline" size="sm" iconName="Settings" iconPosition="left">
          Alert Settings
        </Button>
      </div>

      <div className="space-y-3">
        {upcomingDeadlines.map((deadline) => {
          const daysUntil = getDaysUntilDeadline(deadline.date);
          const isOverdue = daysUntil < 0;
          const isUrgent = daysUntil <= 3 && daysUntil >= 0;

          return (
            <div
              key={deadline.id}
              className={`p-4 rounded-lg border transition-all hover:shadow-sm ${
                isOverdue ? 'bg-error/5 border-error/20' : isUrgent ?'bg-warning/5 border-warning/20': 'bg-muted/30 border-border'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={getTypeIcon(deadline.type)} size={16} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-foreground">{deadline.title}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(deadline.priority)}`}>
                        {deadline.priority.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{deadline.description}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-foreground font-medium">
                        Due: {formatDate(deadline.date)}
                      </span>
                      <span className={`font-medium ${
                        isOverdue ? 'text-error' : isUrgent ?'text-warning': 'text-success'
                      }`}>
                        {isOverdue ? `${Math.abs(daysUntil)} days overdue` :
                         daysUntil === 0 ? 'Due today' :
                         `${daysUntil} days remaining`}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" iconName="Bell">
                    Remind
                  </Button>
                  <Button variant="outline" size="sm" iconName="ExternalLink">
                    View
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing next 5 deadlines • {upcomingDeadlines.filter(d => getDaysUntilDeadline(d.date) <= 3).length} urgent
          </div>
          <Button variant="ghost" size="sm" iconName="Calendar">
            View Full Calendar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ComplianceCalendar;